import { useRef, useState, MouseEvent } from 'react'
import { LuEllipsisVertical, LuPencil, LuTrash } from 'react-icons/lu'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import DeleteConfirm from './DeleteConfirm'

type Props = {
  name: string
  isDirectory: boolean
  onRename: () => void
  onDelete: () => void
}

export default function FileRowMenu({ name, isDirectory, onRename, onDelete }: Props) {
  // Capture the click position rather than using an element anchor. Our row
  // hover CSS hides the ⋮ button once the menu opens (focus leaves the row),
  // which would make an element-anchored menu jump to (0, 0). A position
  // anchor captured at click time stays put.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const deleteTriggerRef = useRef<HTMLSpanElement>(null)

  const open = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    setPos({ top: rect.bottom, left: rect.right })
  }
  // Move focus off the MenuItem before MUI marks the Menu aria-hidden during
  // its close animation — otherwise the browser warns about a focused
  // descendant inside an aria-hidden ancestor.
  const close = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
    setPos(null)
  }

  const handleRename = (e: MouseEvent) => {
    e.stopPropagation()
    close()
    onRename()
  }

  // Defer opening the confirm until after this click finishes bubbling —
  // otherwise the in-flight event reaches the freshly-rendered Dialog root
  // and MUI interprets it as a close-click.
  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation()
    close()
    setTimeout(() => deleteTriggerRef.current?.click(), 0)
  }

  const deleteMessage = isDirectory
    ? `"${name}" and everything inside will be permanently deleted.`
    : `"${name}" will be permanently deleted.`

  // Menu renders via a React portal, so its click events bubble through the
  // React tree up to the enclosing ListItemButton. Stop both mousedown (for
  // the row's ripple highlight) and click (for its navigation/download
  // handler) at this wrapper so nothing leaks out.
  const stop = (e: MouseEvent) => e.stopPropagation()

  // The hidden trigger below lives inside the enclosing row, which for
  // folders is a React Router <Link> rendered as an <a>. A programmatic
  // click on this span bubbles up the DOM through that <a>, and because our
  // outer stopPropagation blocks React Router's onClick (which would call
  // preventDefault), the browser would otherwise follow the href. Calling
  // preventDefault here cancels the anchor navigation without affecting
  // DeleteConfirm's own click handler, which runs first.
  const trigger = (e: MouseEvent) => e.preventDefault()

  return (
    <span onClick={stop} onMouseDown={stop}>
      <IconButton size="small" onClick={open} aria-label="More">
        <LuEllipsisVertical />
      </IconButton>
      <Menu
        open={!!pos}
        onClose={close}
        anchorReference="anchorPosition"
        anchorPosition={pos ?? undefined}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleRename}>
          <ListItemIcon>
            <LuPencil />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <LuTrash />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      <DeleteConfirm message={deleteMessage} onConfirm={onDelete}>
        <span ref={deleteTriggerRef} onClick={trigger} style={{ display: 'none' }} aria-hidden />
      </DeleteConfirm>
    </span>
  )
}
